<template>
	<template v-if="allowed">
		<template v-if="pop">
			<anchor-element v-if="hover" :tag="SymIcon" class="form-help-icon" :chain="chain" :name="iconAlways" @pointerenter="onEnter" @pointerleave="onLeave" :style="marginStyle" v-bind="$attrs"></anchor-element>
			<anchor-element v-else :tag="SymIcon" class="form-help-icon" :chain="chain" :name="iconAlways" @pointerdown="onDown" @pointerup="onUp" @click.prevent.stop="" :style="marginStyle" v-bind="$attrs"></anchor-element>
			<boat-element tag="div" class="bubble form-help-bubble" :chain="chain">
				<span v-if="html" v-html="message"></span>
				<span v-else>{{ message }}</span>
			</boat-element>
		</template>
		<template v-else>
			<div class="formHelp fh" v-bind="$attrs">
				<sym-icon v-if="icon" :name="icon"></sym-icon>
				<span v-if="html" v-html="message"></span>
				<span v-else>{{ message }}</span>
			</div>
		</template>
	</template>
</template>

<script setup lang="ts">
import {computed, type CSSProperties, ref} from "vue";
import SymIcon from "@fd/helpers/SymIcon.vue";
import type {Help, HelpDisplay} from "@t/fd/layout/Widget.ts";
import {ConcreteDisplay} from "@t/fd/layout/FormLayout.ts";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import {ChainRule, createChain} from "@/ui/paneltree/PanelTree.ts";
import {ElementMeasure} from "@fd/helpers/DomUtils.ts";

export type HelpPlacement = "lab" | "fld" | "dir";

const props = withDefaults(defineProps<{
	value: string | Help,
	/* Placement of this component */
	for: HelpPlacement | HelpPlacement[],
	/* Display type (of widget) */
	dt: ConcreteDisplay,
	margin?: "left" | "right" | "inline" | "no",
	above?: boolean
}>(), { for: "fld", margin: "left", above: false });

// Test if the device supports mouse hovering
const hover = ref<boolean>(window.matchMedia('(hover: hover)').matches);

const icon = computed<string>(() => typeof props.value === "string" ? null : props.value.icon);
const iconAlways = computed<string>(() => {
	return typeof props.value === "string" || !props.value.icon ? "info-16" : props.value.icon
});
const message = computed<string>(() => typeof props.value === "string" ? props.value : props.value.message);
const pop = computed<boolean>(() => typeof props.value === "string" || props.value.display !== "direct");
const html = computed<boolean>(() => typeof props.value === "string" ? false : !!props.value.html);

const marginStyle = computed<CSSProperties|null>(() => {
	switch (props.margin) {
		case "left": return { marginLeft: "var(--input-spacing)"};
		case "right": return { marginRight: "var(--input-spacing)"};
		case "inline": return { marginInline: "var(--input-spacing)"};
		case "no": return null;
	}
});

function automaticPlacementFor(disp: ConcreteDisplay):HelpPlacement {
	switch (disp) {
		case "standard":
		case "flow":
		case "spaced-reverse":
		case "field":
		case "input-inline":
			return "fld";
		case "stacked":
		case "checkbox-push":
		case "stacked-push":
		case "checkbox-pull":
		case "flow-reverse":
		case "spaced":
		case "openable":
		case "checkbox-inline":
		case "picked":
			return "lab";
		default:
			return "dir";
	}
}

function isFor(placement: HelpPlacement):boolean {
	if (Array.isArray(props.for)) {
		return props.for.findIndex((element: HelpPlacement) => element === placement) >= 0;
	}
	return props.for === placement;
}

const allowed = computed<boolean>(() => {
	if (typeof props.value === "string") {
		return isFor("fld");
	} else {
		// How should be the help displayed
		const disp:HelpDisplay = props.value.display == null ? "auto" : props.value.display;
		switch (disp) {
			case "label":
				return isFor("lab");
			case "field":
				return isFor("fld");
			case "direct":
				return isFor("dir");
			case "auto":
			default:
				return isFor(automaticPlacementFor(props.dt));
		}
	}
});

const chain = allowed.value && pop.value ? createChain({
	rule: (m:ElementMeasure) => {
		const above:ChainRule = {
			ah: 50, av: 0,
			bh: 50, bv: 100,
			x: 0, y: 0, tail: "bottom-center" };
		const bellow:ChainRule = {
			ah: 50, av: 100,
			bh: 50, bv: 0,
			x: 0, y: 0 , tail: "top-center" };

		if (props.above) {
			if (m.spaceTop < 100 && m.spaceBottom > m.spaceTop) {
				return bellow;
			} else {
				return above;
			}
		} else {
			if (m.spaceBottom < 100 && m.spaceTop > m.spaceBottom) {
				return above;
			} else {
				return bellow;
			}
		}
	}
}, "popup-help") : null;


function onEnter(e:PointerEvent) {
	chain.visible.value = true;
}
function onLeave(e:PointerEvent) {
	chain.visible.value = false;
}

let downEvent = 0;
function onDown(e:PointerEvent) {
	chain.toggle();
	e.stopPropagation();
	e.preventDefault();
	downEvent = e.timeStamp;
}
function onUp(e:PointerEvent) {
	if ((e.timeStamp - downEvent) > 1000) chain.visible.value = false;
	e.stopPropagation();
	e.preventDefault();
}

</script>

<style>

.bubble {
	max-width: 20em;
	text-wrap: balance;
	background-color: var(--bg);
	color: var(--fg);
	border: 1px solid var(--border-color);
	padding: 10px;
	border-radius: var(--menu-radius);
	position: relative;
	box-sizing: border-box;
	box-shadow: var(--panel-shadow);
}

.fh {
	display: flex;
	flex-direction: row;
	align-items: baseline;
	justify-content: flex-start;
	column-gap: 4px;
	max-width: 30em;
	margin-top: 4px;
	font-weight: normal;
	font-style: normal;
	text-wrap: balance;

	&.fh-no-margin {
		margin-top: 0;
	}
}
.form-help-icon {
	opacity: 0.5;
	cursor: default;
	user-select: none;
	-webkit-user-select: none;

	&:hover {
		opacity: 1;
	}
}
.form-help-bubble {
	font-size: var(--font-size-s);
	pointer-events: none;
}
</style>